import React, { useState } from 'react';
import { DownloadIcon, EyeIcon, PlusIcon, PrinterIcon, SearchIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CreditNote, Patient, WalkInClient } from '../../types';
import { useData } from '../../store/DataContext';
import { BillingDocumentPreviewModal, DocumentPreviewAction } from './BillingDocumentPreviewModal';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const CreditNotes: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    creditNotes,
    patients,
    walkInClients,
    invoices
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNote | null>(null);
  const [previewAction, setPreviewAction] = useState<DocumentPreviewAction>('view');
  const openPreview = (creditNote: CreditNote, action: DocumentPreviewAction) => {
    setPreviewAction(action);
    setSelectedCreditNote(creditNote);
  };
  const filteredCreditNotes = creditNotes.filter(cn => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (cn.creditNoteNumber.toLowerCase().includes(query)) return true;
    let customerName = '';
    if (cn.customerType === 'Patient') {
      const p = patients.find(p => p.id === cn.customerId);
      if (p) customerName = `${p.firstName} ${p.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === cn.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(query);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Issued':
        return 'bg-blue-100 text-blue-800';
      case 'Applied':
        return 'bg-green-100 text-green-800';
      case 'Refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1151" data-mp="2fj0gk5h5zrjt">
      <div className="flex justify-between items-center" data-id="element-1152" data-mp="2fja6jt27lypi">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1153" data-mp="2bg43euae8nn9">Credit Notes</h1>
        <Button onClick={() => onNavigate('new-credit-note' as any)} data-id="element-1154" data-mp="26h0zjd65gngv">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1155" data-mp="26jdgbn068rsc" /> New Credit Note
        </Button>
      </div>

      <Card noPadding data-id="element-1156" data-mp="21k0sayxkdeo6">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1157" data-mp="4pw9gljv7uyn">
          <div className="relative max-w-md" data-id="element-1158" data-mp="4p4py93jlnoq">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1159" data-mp="3vwgzc1y6owu3">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1160" data-mp="7alz8l192tyi" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by credit note number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1161" data-mp="78hrfs08dasy" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1162" data-mp="3pg79xszrqwji">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1163" data-mp="3ueikk5xw1b2s">
            <thead className="bg-gray-50" data-id="element-1164" data-mp="3uh4o7uhnr6fq">
              <tr data-id="element-1165" data-mp="390nx44d6nqf">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1166" data-mp="39ifhng30ds0">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1167" data-mp="1ennnqsnbv9t5">
                  Credit Note#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1168" data-mp="1c9isdjj9w85q">
                  Invoice#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1169" data-mp="1m7orxuhlx27f">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1170" data-mp="1haokbf53zre7">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1171" data-mp="1r5q8o8rk8pfq">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1172" data-mp="1zz39rxzs2xcw">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1173" data-mp="29x9eencyyhlg">
              {filteredCreditNotes.length > 0 ? filteredCreditNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(cn => {
              const inv = invoices.find(i => i.id === cn.invoiceId);
              return <tr key={cn.id} className="hover:bg-gray-50" data-id="element-1174" data-mp="2ohvbt6ssnb6r">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1175" data-mp="29tr38vnggg1p">
                          {new Date(cn.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1176" data-mp="20ung9gl63ghx">
                          {cn.creditNoteNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1177" data-mp="1qw7qpi1sgfaj">
                          {inv?.invoiceNumber || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1178" data-mp="1vx55dknlar7a">
                          {getCustomerName(cn.customerId, cn.customerType)}
                          <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1179" data-mp="17y4lphelwt6i">
                            ({cn.customerType})
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1180" data-mp="y32umg0rth1a">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(cn.status)}`} data-id="element-1181" data-mp="1vo8lfdooc8s8">
                            {cn.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1182" data-mp="1gssqz7k6mavb">
                          {formatCurrency(cn.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1183" data-mp="5ckrjf01rfnv">
                          <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View credit note" aria-label={`View credit note ${cn.creditNoteNumber}`} onClick={() => openPreview(cn, 'view')} data-id="element-1184" data-mp="2h0z6k5jmkfwr">
                            <EyeIcon className="h-4 w-4" data-id="element-1185" data-mp="30qj9x0gy0vxe" />
                          </button>
                          <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Print credit note" aria-label={`Print credit note ${cn.creditNoteNumber}`} onClick={() => openPreview(cn, 'print')} data-id="element-1186" data-mp="34rv1hbjdnr88">
                            <PrinterIcon className="h-4 w-4" data-id="element-1187" data-mp="3m6fht0x1vsuh" />
                          </button>
                          <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Download credit note PDF" aria-label={`Download credit note ${cn.creditNoteNumber} as PDF`} onClick={() => openPreview(cn, 'download')} data-id="element-1188" data-mp="3h7cmw9asb308">
                            <DownloadIcon className="h-4 w-4" data-id="element-1189" data-mp="3d6j1pnoq82b1" />
                          </button>
                        </td>
                      </tr>;
            }) : <tr data-id="element-1190" data-mp="sg1yajsufj1k">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1191" data-mp="sf8qdfi5enhu">
                    No credit notes found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedCreditNote && <BillingDocumentPreviewModal record={selectedCreditNote} documentType="Credit Note" customer={getCreditNoteCustomer(selectedCreditNote, patients, walkInClients)} relatedInvoice={invoices.find(invoice => invoice.id === selectedCreditNote.invoiceId)} initialAction={previewAction} onClose={() => setSelectedCreditNote(null)} data-id="element-1192" data-mp="zxq3dvp8gkfk" />}
    </div>;
};
function getCreditNoteCustomer(creditNote: CreditNote, patients: Patient[], walkInClients: WalkInClient[]) {
  if (creditNote.customerType === 'Patient') {
    const patient = patients.find(entry => entry.id === creditNote.customerId);
    return {
      name: patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient',
      fileNumber: patient?.bmcFileNumber,
      address: patient?.address,
      contact: patient?.contact
    };
  }
  const walkInClient = walkInClients.find(entry => entry.id === creditNote.customerId);
  return {
    name: walkInClient?.name || 'Walk-in client',
    address: walkInClient?.address,
    contact: walkInClient?.phone
  };
}